import React from "react";
import {
  Phone, Mail, Clock, MapPin, Building2, Globe, User, Briefcase,
  Calendar, Package, MessageCircle, Twitter, Facebook, ExternalLink,
  Tag,
} from "lucide-react";
import { useCompany } from "../context/CompanyContext";

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <div className="w-8 h-8 rounded-md bg-[#eef6f7] text-[#007a8a] flex items-center justify-center shrink-0">
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        <div className="text-sm text-gray-800 mt-0.5 break-words">{children}</div>
      </div>
    </div>
  );
}

export default function CompanyDetails() {
  const { company } = useCompany();
  const d = company.details;
  const cs = d.customerSupport;
  return (
    <section className="bg-white border-b border-gray-200">
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <h2 className="text-lg md:text-2xl font-bold text-gray-900">About {company.name}</h2>
          <a
            href={d.website}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-[#007a8a] hover:underline inline-flex items-center gap-1"
          >
            Visit official site <ExternalLink size={13}/>
          </a>
        </div>

        <p className="text-sm text-gray-700 leading-relaxed mb-5">{d.introBlurb}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-[#f9fbfc] rounded-lg border border-gray-200 p-4">
            <div className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-1.5">
              <Phone size={15} className="text-[#007a8a]"/> Customer Support
            </div>
            <p className="text-xs text-gray-500 mb-2">Most-used contact options.</p>
            <DetailRow icon={Phone} label="Phone">
              <a href={`tel:${String(cs.phone).replace(/\s/g, "")}`} className="text-[#007a8a] hover:underline font-medium">
                {cs.phone}
              </a>
            </DetailRow>
            <DetailRow icon={Clock} label="Opening Hours">{cs.hours}</DetailRow>
            <DetailRow icon={MessageCircle} label="Live Chat">{cs.chat}</DetailRow>
            <DetailRow icon={Mail} label="Email">
              {cs.email.includes("@") ? (
                <a href={`mailto:${cs.email}`} className="text-[#007a8a] hover:underline">{cs.email}</a>
              ) : (
                <span>{cs.email}</span>
              )}
            </DetailRow>
            <DetailRow icon={Twitter} label="X / Twitter">{cs.twitter}</DetailRow>
            <DetailRow icon={Facebook} label="Facebook">{cs.facebook}</DetailRow>
          </div>

          <div className="bg-[#f9fbfc] rounded-lg border border-gray-200 p-4">
            <div className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-1.5">
              <Building2 size={15} className="text-[#007a8a]"/> Company Information
            </div>
            <p className="text-xs text-gray-500 mb-2">Headquarters and key facts.</p>
            <DetailRow icon={Briefcase} label="Legal Name">{d.legalName}</DetailRow>
            <DetailRow icon={Tag} label="Industry">{d.industry}</DetailRow>
            <DetailRow icon={Calendar} label="Founded">{d.founded}</DetailRow>
            <DetailRow icon={User} label="CEO">{d.ceo}</DetailRow>
            <DetailRow icon={Building2} label="Employees">{d.employees}</DetailRow>
            <DetailRow icon={Package} label="Volume / scale">{d.parcelsPerYear}</DetailRow>
          </div>

          <div className="bg-[#f9fbfc] rounded-lg border border-gray-200 p-4">
            <div className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-1.5">
              <MapPin size={15} className="text-[#007a8a]"/> Address & Services
            </div>
            <p className="text-xs text-gray-500 mb-2">Where to write and what they do.</p>
            <DetailRow icon={MapPin} label="Headquarters">{d.headquarters}</DetailRow>
            <DetailRow icon={Mail} label="Postal Address">{d.postalAddress}</DetailRow>
            <DetailRow icon={Globe} label="Website">
              <a href={d.website} target="_blank" rel="noreferrer" className="text-[#007a8a] hover:underline">
                {company.url}
              </a>
            </DetailRow>
            {d.trackingUrl && (
              <DetailRow icon={Package} label="Online Account / Tracking">
                <a href={d.trackingUrl} target="_blank" rel="noreferrer" className="text-[#007a8a] hover:underline break-all">
                  {d.trackingUrl.replace(/^https?:\/\//, "")}
                </a>
              </DetailRow>
            )}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <div className="text-sm font-bold text-gray-900 mb-2">Services Offered</div>
            <ul className="space-y-1.5">
              {d.services.map((s) => (
                <li key={s} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-[#007a8a] mt-1.5 w-1.5 h-1.5 rounded-full bg-[#007a8a] shrink-0"/>
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900 mb-2">Popular Searches</div>
            <div className="flex flex-wrap gap-2">
              {d.popularSearches.map((p) => (
                <a
                  key={p}
                  href="#"
                  className="text-xs px-3 py-1.5 rounded-full bg-[#f5f7f8] text-gray-700 border border-gray-200 hover:bg-[#eef6f7] hover:border-[#007a8a] hover:text-[#007a8a] transition-colors"
                >
                  {p}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
